import { clamp } from 'lodash';

import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { ImageModalZoomDimensions } from './imageModalZoomTypes';

/** How far past the viewport edge an image edge may be dragged before the pan is clamped. */
export const IMAGE_MODAL_PAN_EDGE_MARGIN_PX = 100;

export type PanTranslationRange = {
    min: number;
    max: number;
};

/** How far each content edge sits outside the viewport at a given translation. */
export type PanEdgeOutsideDistances = {
    left: number;
    right: number;
    top: number;
    bottom: number;
};

/** Pan limits persisted after zoom until scale changes again. */
export type ImageModalPanEdgeSession = {
    settleTranslation: Point;
    /** Max outside distance allowed on every edge (the largest value at settle, at least the margin). */
    outsideAllowance: number;
};

/**
 * Widens a pan range so it includes the settle translation, expanding the opposite edge by the same
 * amount so the allowance stays symmetric about the centre.
 */
const includeSettleTranslationSymmetrically = (
    range: PanTranslationRange,
    settleTranslation: number,
): PanTranslationRange => {
    let { min, max } = range;

    if (settleTranslation < min) {
        const delta = min - settleTranslation;
        max += delta;
        min = settleTranslation;
    }

    if (settleTranslation > max) {
        const delta = settleTranslation - max;
        min -= delta;
        max = settleTranslation;
    }

    return { min, max };
};

/**
 * Half the amount the scaled content overflows the viewport on one axis.
 */
const getAxisExcess = (viewportDimension: number, contentDimension: number, scale: number): number =>
    (contentDimension * scale - viewportDimension) / 2;

/**
 * Standard pan range: every edge can reach the viewport edge and up to `margin` px beyond it.
 */
const getStandardPanTranslationRangeFromExcess = (
    excess: number,
    margin = IMAGE_MODAL_PAN_EDGE_MARGIN_PX,
): PanTranslationRange => {
    const marginMin = margin - excess;
    const marginMax = excess - margin;
    const flushMin = -excess;
    const flushMax = excess;

    return {
        min: Math.min(marginMin, marginMax, flushMin, flushMax),
        max: Math.max(marginMin, marginMax, flushMin, flushMax),
    };
};

/**
 * Pan limits for one axis: each edge can sit at most `margin` px outside the viewport.
 */
export const getPanTranslationRange = (
    viewportDimension: number,
    contentDimension: number,
    scale: number,
    margin = IMAGE_MODAL_PAN_EDGE_MARGIN_PX,
): PanTranslationRange =>
    getStandardPanTranslationRangeFromExcess(getAxisExcess(viewportDimension, contentDimension, scale), margin);

/**
 * How far each content edge sits outside the viewport at the given translation.
 */
export const getPanEdgeOutsideDistances = (
    translation: Point,
    { scale, viewportSize, contentSize }: ImageModalZoomDimensions & { scale: number },
): PanEdgeOutsideDistances => {
    const xExcess = getAxisExcess(viewportSize.width, contentSize.width, scale);
    const yExcess = getAxisExcess(viewportSize.height, contentSize.height, scale);

    return {
        left: Math.max(0, xExcess - translation.x),
        right: Math.max(0, xExcess + translation.x),
        top: Math.max(0, yExcess - translation.y),
        bottom: Math.max(0, yExcess + translation.y),
    };
};

/**
 * Whether a settle translation sits beyond the standard flush + margin limits.
 */
const isOutsideStandardPanRange = (
    settleTranslation: number,
    excess: number,
    margin = IMAGE_MODAL_PAN_EDGE_MARGIN_PX,
): boolean => {
    const standard = getStandardPanTranslationRangeFromExcess(excess, margin);

    return settleTranslation < standard.min || settleTranslation > standard.max;
};

/**
 * Per-axis pan limits while a pan edge session is active.
 */
const getSessionPanRangeForAxis = (
    excess: number,
    settleTranslation: number,
    outsideAllowance: number,
    margin = IMAGE_MODAL_PAN_EDGE_MARGIN_PX,
): PanTranslationRange => {
    const standard = getStandardPanTranslationRangeFromExcess(excess, margin);

    if (!isOutsideStandardPanRange(settleTranslation, excess, margin)) return standard;

    let range = standard;

    if (outsideAllowance > margin && excess > 0 && outsideAllowance > excess) {
        const allowanceMin = excess - outsideAllowance;
        const allowanceMax = outsideAllowance - excess;

        if (allowanceMin <= allowanceMax) {
            range = {
                min: Math.max(standard.min, allowanceMin),
                max: Math.min(standard.max, allowanceMax),
            };
        }
    }

    return includeSettleTranslationSymmetrically(range, settleTranslation);
};

/**
 * Resolves the active pan limits for each axis.
 */
export const getEffectivePanTranslationRanges = (
    { scale, viewportSize, contentSize }: ImageModalZoomDimensions & { scale: number },
    panEdgeSession: ImageModalPanEdgeSession | null,
    margin = IMAGE_MODAL_PAN_EDGE_MARGIN_PX,
): { x: PanTranslationRange; y: PanTranslationRange } => {
    const xExcess = getAxisExcess(viewportSize.width, contentSize.width, scale);
    const yExcess = getAxisExcess(viewportSize.height, contentSize.height, scale);

    if (!panEdgeSession) {
        return {
            x: getPanTranslationRange(viewportSize.width, contentSize.width, scale, margin),
            y: getPanTranslationRange(viewportSize.height, contentSize.height, scale, margin),
        };
    }

    return {
        x: getSessionPanRangeForAxis(
            xExcess,
            panEdgeSession.settleTranslation.x,
            panEdgeSession.outsideAllowance,
            margin,
        ),
        y: getSessionPanRangeForAxis(
            yExcess,
            panEdgeSession.settleTranslation.y,
            panEdgeSession.outsideAllowance,
            margin,
        ),
    };
};

/**
 * Records a pan edge session when zoom settles outside the standard pan range.
 */
export const getPanEdgeSessionForTranslation = (
    translation: Point,
    dimensions: ImageModalZoomDimensions & { scale: number },
    margin = IMAGE_MODAL_PAN_EDGE_MARGIN_PX,
): ImageModalPanEdgeSession | null => {
    const { x, y } = getEffectivePanTranslationRanges(dimensions, null, margin);
    const outsideStandardX = translation.x < x.min || translation.x > x.max;
    const outsideStandardY = translation.y < y.min || translation.y > y.max;

    if (!outsideStandardX && !outsideStandardY) return null;

    const outside = getPanEdgeOutsideDistances(translation, dimensions);
    const maxOutside = Math.max(outside.left, outside.right, outside.top, outside.bottom);

    return {
        settleTranslation: { ...translation },
        outsideAllowance: Math.max(margin, maxOutside),
    };
};

export type PanEdgeSessionManager = ReturnType<typeof createPanEdgeSessionManager>;

/**
 * Owns the pan edge session lifecycle.
 */
export const createPanEdgeSessionManager = () => {
    let session: ImageModalPanEdgeSession | null = null;

    return {
        getSession: (): ImageModalPanEdgeSession | null => session,
        clear: (): void => {
            session = null;
        },
        commit: (translation: Point, dimensions: ImageModalZoomDimensions & { scale: number }): void => {
            session = getPanEdgeSessionForTranslation(translation, dimensions);
        },
        refreshForViewport: (
            translation: Point,
            dimensions: ImageModalZoomDimensions & { scale: number },
        ): ImageModalPanEdgeSession | null => {
            session = getPanEdgeSessionForTranslation(translation, dimensions);
            return session;
        },
    };
};

/**
 * Clamps pan offset so image edges stay within the viewport margin.
 */
export const clampPanToBounds = (
    translation: Point,
    dimensions: ImageModalZoomDimensions & { scale: number },
    options: { panEdgeSession?: ImageModalPanEdgeSession | null; margin?: number } = {},
): Point => {
    const { panEdgeSession = null, margin = IMAGE_MODAL_PAN_EDGE_MARGIN_PX } = options;
    const ranges = getEffectivePanTranslationRanges(dimensions, panEdgeSession, margin);

    return {
        x: clamp(translation.x, ranges.x.min, ranges.x.max),
        y: clamp(translation.y, ranges.y.min, ranges.y.max),
    };
};
